import React, { useContext, useMemo } from 'react';
import { MedicationProductCodeKey } from '@dayetopia/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CartItemPriceDisplay from '@components/cart/CartItemPriceDisplay';
import CartItemRow from '@components/cart/CartItemRow';
import CartItemImage from '@components/cart/ProductImage';
import RemoveFromCart from '@components/cart/RemoveFromCart';
import { Context } from '@components/Context';
import CartItemBoxItems from '@components/dashboard/CartItemBoxItems';
import { getTreatmentById } from '@components/dashboard/data/TreatmentsData';
import Colors from '@components/shared/Colors';
import { TextVariant, Typography } from '@components/shared/design/Typography';
import { CartItem, ProductCode } from '@contracts';
import { getListOfIncludesForProduct, getProductSubtitle, productsData } from '@data/products';
import CartMinus from '@images/cart/cart-minus.svg';
import CartPlus from '@images/cart/cart-plus.svg';
import { hasTamponsAndPads } from '@utils/cart';
import handleTextReplacement from '@utils/handleTextReplacement';
import { getPriceText } from '@utils/price';
import { getProductPricesFromStorage } from '@utils/prices';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Props {
  item: CartItem;
  editable: boolean;
  textColor?: string;
}

const OneOffPurchaseCartItemRow: React.FC<Props> = ({ item, editable, textColor = Colors.navyBlue }) => {
  const { t } = useTranslation();
  const { state, dispatch } = useContext(Context);
  const { currency, country } = state;
  const quantityMax = 99;
  const { quantity } = item;
  const boxItems = item.boxItems?.filter((bi) => bi.key != null) ?? [];
  const boxItemsCount = boxItems.reduce((prev, curr) => (curr.key ? prev + curr.count : prev), 0);

  const quantityDecrease = () => {
    dispatch({ type: 'cartItemQuantityDecrease', data: item });
  };

  const quantityIncrease = () => {
    dispatch({ type: 'cartItemQuantityIncrease', data: item });
  };

  const titleText = useMemo(() => {
    const pricesData = getProductPricesFromStorage();
    const product =
      pricesData && Array.isArray(pricesData)
        ? pricesData.find((priceItem) => {
            if (item.productCode === ProductCode.VMSMedicationFee && item.id !== ProductCode.VMSMedicationFee) {
              return priceItem.id === item.id;
            }
            return priceItem.id === item.productCode;
          })
        : null;

    if (product && product.name) {
      return product.name;
    }

    const productData = productsData.find((product) => product.id === item.productCode);
    if (productData) {
      if (productData.id === ProductCode.VMSMedicationFee) {
        const treatment = getTreatmentById(item.id as MedicationProductCodeKey);
        if (treatment) return treatment.title;
      }
      return productData.title;
    }
    return item.productCode;
  }, [item, currency]);

  const subtitleText = useMemo(() => getProductSubtitle(item.productCode), [item]);
  const includesList = useMemo(() => getListOfIncludesForProduct(item.productCode), [item]);
  const titleTextRef = useReplacedInnerHtml(titleText);
  const subtitleTextRef = useReplacedInnerHtml(subtitleText);

  const priceTextValue = () => {
    const aggregatedPrice = (item.price ?? 0) * (item.quantity ?? 1);
    return aggregatedPrice > 0 ? getPriceText(aggregatedPrice, country, currency, t).fullString : t('components.free-in-cart');
  };

  const originalPriceTextValue = () => {
    const aggregatedOriginalPrice = (item.originalPrice ?? 0) * (item.quantity ?? 1);
    return aggregatedOriginalPrice > 0 && (item.price ?? 0) < (item.originalPrice ?? 0)
      ? getPriceText(aggregatedOriginalPrice, country, currency, t).fullString
      : '';
  };

  const getImageVersion = (item: CartItem) => {
    if (hasTamponsAndPads(item)) return 2;
    if (item.productCode === ProductCode.VMSMedicationFee) return item.id;
    return 1;
  };

  return (
    <CartItemRow
      imageComponent={<CartItemImage id={item.productCode} imageVersion={getImageVersion(item)} />}
      titleComponent={
        <ContainerDiv>
          <TitleContainer>
            {!item.unique && quantity && <CartItemQuantity>{quantity} &times;</CartItemQuantity>}
            <ProductText>
              <span ref={titleTextRef} />
              <span className="subtitle" ref={subtitleTextRef} />
              {includesList.length > 0 && (
                <IncludesText>
                  {includesList.map((includeItem, index) => (
                    <li key={index}>
                      <Typography $variant={TextVariant.BodyBoldSM}>{handleTextReplacement(includeItem)}</Typography>
                    </li>
                  ))}
                </IncludesText>
              )}
            </ProductText>
          </TitleContainer>
        </ContainerDiv>
      }
      priceComponent={
        <InlineControls>
          {editable && (
            <QuantityControls>
              <ControlButton disabled={quantity <= 1} onClick={quantityDecrease}>
                <CartMinus />
              </ControlButton>
              <ControlButton disabled={quantity >= quantityMax} onClick={quantityIncrease}>
                <CartPlus />
              </ControlButton>
            </QuantityControls>
          )}
          <CartItemPriceDisplay price={priceTextValue()} originalPrice={originalPriceTextValue()} textColor={textColor} />
          {editable && <RemoveFromCart item={item} editable={editable} />}
        </InlineControls>
      }
      extraContent={boxItemsCount > 1 ? <CartItemBoxItems item={item} /> : null}
    />
  );
};

export default OneOffPurchaseCartItemRow;

const ContainerDiv = styled.div`
  width: 100%;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const CartItemQuantity = styled.span`
  font-family: 'BrownStd';
  font-size: 16px;
  line-height: 22px;
  opacity: 0.3;
  margin-right: 5px;
  min-width: 23px;
  display: inline-block;
  font-weight: normal;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-right: 4px;
    min-width: 20px;
  }
`;

const ProductText = styled.h5`
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 22px;
  font-weight: normal;
  font-family: 'BrownStd';
  color: inherit;

  .subtitle {
    font-size: 13px;
    line-height: 16px;
    opacity: 0.5;
    margin-top: 4px;

    &:empty {
      display: none;
    }
  }
`;

const IncludesText = styled.ul`
  font-size: 12px;
  margin-top: 4px;
  list-style-type: disc;
  margin-bottom: 0;
  padding: 0;
  line-height: 1;

  li {
    line-height: 1;
    opacity: 0.7;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const InlineControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const ControlButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: 0 1px;
  background: #e5f2e350;
  height: 26px;
  width: 26px;

  &:disabled svg {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;
